import { ApplicationState, useAppStore } from '../store';
import {
  Connections,
  GetUserId,
  SuccessMessage,
  getActivity,
  getAthlete,
  getBody,
  getDaily,
  getNutrition,
  getSleep,
  getUserId,
  initTerra,
  initConnection as initTerraConnection
} from 'terra-react';
import { useCallback, useEffect } from 'react';

import { Config } from '../constants/Config';
import { Platform } from 'react-native';
import { TerraProvider } from '../types/TerraProvider';
import WearableService from '../../api/wearable';
import moment from 'moment';
import { useGetExternalUserId } from './useUser';
import { useTelemetry } from './useTelemetry';

interface UseWearableSdk {
  isUserAuth: (provider?: TerraProvider) => Promise<boolean>;
  getUserAuth: (provider?: TerraProvider) => Promise<string>;
  requestSyncTerraData: (forceSync: boolean, connection?: Connections) => Promise<boolean>;
  initConnection: (provider: TerraProvider) => Promise<boolean>;
  initWearableSdk: (ignoreCheckingProviderConnection?: boolean) => Promise<boolean>;
}

const ANDROID_NATIVE_PROVIDER_CONNECTIONS: Record<string, Connections> = {
  samsung: Connections.SAMSUNG,
  health_connect: Connections.HEALTH_CONNECT
};

const getTerraConnectionForProvider = (provider?: TerraProvider): Connections | null => {
  if (!provider) {
    return null;
  }

  if (Platform.OS === 'ios') {
    return Connections.APPLE_HEALTH;
  }

  if (Platform.OS !== 'android') {
    return null;
  }

  return ANDROID_NATIVE_PROVIDER_CONNECTIONS[provider.resource] ?? null;
};

const isNativeTerraProvider = (provider?: TerraProvider): boolean => {
  return getTerraConnectionForProvider(provider) != null;
};

const ANDROID_AUTH_CONNECTION_PRIORITY: Connections[] = [
  Connections.SAMSUNG,
  Connections.HEALTH_CONNECT,
  Connections.GOOGLE
];

export const useWearableSdk = (): UseWearableSdk => {
  const { addTelemetryLog } = useTelemetry();
  const initialized = useAppStore((state: ApplicationState) => state.wearableSdkInitialized);
  const setWearableSdkInitialized = useAppStore((state: ApplicationState) => state.setWearableSdkInitialized);
  const setLastSyncDate = useAppStore((state: ApplicationState) => state.setLastSyncDate);
  const setWearableProviders = useAppStore((state: ApplicationState) => state.setWearableProviders);

  const externalUserId = useGetExternalUserId();

  const getUserAuth = useCallback((provider?: TerraProvider) => {
    const connection = getTerraConnectionForProvider(provider) ?? Connections.APPLE_HEALTH;
    return getUserId(connection).then((data: GetUserId) => {
      if (data.success && data.userId) return Promise.resolve(data.userId.toString());
      return Promise.reject();
    });
  }, []);

  const isUserAuth = useCallback(
    (provider?: TerraProvider) => {
      const connection = getTerraConnectionForProvider(provider);
      if (connection != null) {
        return getUserAuth(provider).then(
          () => true,
          () => false
        );
      }

      if (Platform.OS === 'android') {
        return Promise.any(
          ANDROID_AUTH_CONNECTION_PRIORITY.map((androidConnection) =>
            getUserId(androidConnection).then((data: GetUserId) => {
              if (data.success && data.userId) {
                return data.userId.toString();
              }
              return Promise.reject();
            })
          )
        )
          .then(() => true)
          .catch(() => false);
      }

      return getUserAuth().then(
        () => true,
        () => false
      );
    },
    [getUserAuth]
  );

  const requestSyncTerraData = useCallback(
    async (forceSync: boolean, connection: Connections = Connections.APPLE_HEALTH) => {
      const days = 90;

      const neededSync: Promise<boolean> = forceSync ? Promise.resolve(true) : WearableService.checkIfSyncNeeded();

      const startDate = moment().subtract(days, 'days').toDate();

      const sendLogLegacy = async (name: string, callback: () => Promise<any>) => {
        try {
          const data = await callback();
          addTelemetryLog(`${name}: ${JSON.stringify(data)}`);
        } catch (err) {
          addTelemetryLog(`${name} - fail ${JSON.stringify(err)}`);
        }
      };

      const requestDataLegacy = async () => {
        await sendLogLegacy('getUserId', async () => getUserId(connection));
        await sendLogLegacy('getActivity', async () => getActivity(connection, startDate, new Date(), true));
        await sendLogLegacy('getBody', async () => getBody(connection, startDate, new Date(), true));
        await sendLogLegacy('getDaily', async () => getDaily(connection, startDate, new Date(), true));
        await sendLogLegacy('getSleep', async () => getSleep(connection, startDate, new Date(), true));
        await sendLogLegacy('getNutrition', async () => getNutrition(connection, startDate, new Date(), true));

        getAthlete(connection, true);

        return true;
      };

      return neededSync.then((sync) => {
        if (sync) {
          return requestDataLegacy();
        }
        return true;
      });
    },
    [addTelemetryLog]
  );

  const initConnectionWithLib = useCallback(
    async (provider: TerraProvider, checkIfSyncNeeded = false): Promise<boolean> => {
      const connection = getTerraConnectionForProvider(provider);
      if (connection == null) {
        addTelemetryLog(`Unsupported Terra provider: ${provider.provider_name}`);
        return false;
      }

      const token = await WearableService.getToken(provider.providerId, provider?.resource);
      addTelemetryLog(`Auth token ${token}`);
      return initTerraConnection(connection, token, true, [])
        .then((data) => {
          addTelemetryLog(`initConnection: ${JSON.stringify(data)}`);
          if (data.success) {
            requestSyncTerraData(checkIfSyncNeeded, connection);
          }
          return data.success.valueOf();
        })
        .catch((error) => {
          addTelemetryLog(`initConnection - fail: ${JSON.stringify(error)}`);
          return false;
        });
    },
    [addTelemetryLog, requestSyncTerraData]
  );

  const initWearableSdk = useCallback(
    async (ignoreCheckingProviderConnection = false): Promise<boolean> => {
      if (
        Config.TERRA_DEV_ID === '-loaded-from-the-backend-' ||
        (Platform.OS !== 'ios' && Platform.OS !== 'android') ||
        initialized ||
        !externalUserId
      ) {
        return false;
      }

      // alway init Terra
      const initResult: SuccessMessage = await initTerra(Config.TERRA_DEV_ID, externalUserId);

      try {
        const providers: TerraProvider[] = await WearableService.getSubscriptions();
        setWearableProviders(providers);

        // set last sync date
        const connectedProviders = providers
          .filter((item: TerraProvider) => {
            return item.connectedDate != undefined && item.disconnectedDate == undefined;
          })
          .sort((a, b) => b.terra_last_webhook_update.getTime() - a.terra_last_webhook_update.getTime());
        if (connectedProviders.length > 0) {
          setLastSyncDate(connectedProviders[0].terra_last_webhook_update);
        }

        const nativeProvider = providers.find(
          (provider: TerraProvider) =>
            isNativeTerraProvider(provider) && provider.disconnectedDate == undefined && provider.connectedDate != null
        );
        if (!nativeProvider) {
          return false;
        }

        addTelemetryLog(`initTerra: ${JSON.stringify(initResult)}`);

        if (initResult.success) {
          initConnectionWithLib(nativeProvider);
          setWearableSdkInitialized(true);
          return true;
        }
      } catch (error) {
        addTelemetryLog(`Error: ${JSON.stringify(error)}`);
      }

      return false;
    },
    [
      addTelemetryLog,
      initConnectionWithLib,
      initialized,
      setWearableSdkInitialized,
      setLastSyncDate,
      externalUserId,
      setWearableProviders
    ]
  );

  const initConnection = async (provider: TerraProvider): Promise<boolean> => {
    if (initialized) {
      return initConnectionWithLib(provider);
    } else {
      return initWearableSdk(true);
    }
  };

  return { getUserAuth, isUserAuth, requestSyncTerraData, initConnection, initWearableSdk };
};

export const useWearableInitializer = () => {
  const isAppInit = useAppStore((state: ApplicationState) => state.isAppInitComplete);
  const { initWearableSdk } = useWearableSdk();

  useEffect(() => {
    if (isAppInit) {
      initWearableSdk();
    }
  }, [isAppInit, initWearableSdk]);
};
